import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MaleIcon from '@mui/icons-material/Male';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import api from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';

const COPY = {
  ar: {
    title: 'إدارة الأفحل',
    subtitle: 'إضافة وتعديل وتعطيل الأفحل التي تظهر في قائمة التلقيح داخل عيادة الإنتاج.',
    add: 'إضافة فحل',
    edit: 'تعديل الفحل',
    refresh: 'تحديث',
    activeList: 'قائمة التلقيح',
    candidates: 'أفحل مسجلة يمكن إضافتها',
    candidatesHint: 'اختر فحلاً من الخيل المسجلة أو أضف فحلاً خارجياً يدوياً.',
    registered: 'الأفحل المدرجة',
    registeredHint: 'هذه القائمة هي التي تظهر عند اختيار الفحل داخل إجراء التلقيح.',
    total: 'إجمالي الأفحل',
    inHouse: 'من المربط',
    external: 'خارجي',
    used: 'استخدامات التلقيح',
    noData: 'لا توجد بيانات.',
    type: 'نوع الفحل',
    selectHorse: 'اختر فحل من الخيل المسجلة',
    nameEn: 'الاسم بالإنجليزية',
    nameAr: 'الاسم بالعربية',
    country: 'بلد المنشأ',
    owner: 'المالك',
    notes: 'ملاحظات',
    status: 'الحالة',
    active: 'نشط',
    inactive: 'غير نشط',
    save: 'حفظ',
    cancel: 'إلغاء',
    deactivate: 'تعطيل',
    deactivateTitle: 'تعطيل الفحل',
    deactivateConfirm: 'سيتم إخفاء الفحل من قائمة التلقيح مع الاحتفاظ بالسجلات السابقة.',
    saved: 'تم حفظ الفحل.',
    deactivated: 'تم تعطيل الفحل.',
    duplicate: 'هذا الفحل مسجل مسبقاً.',
    required: 'الاسم العربي والإنجليزي وبلد المنشأ مطلوبة.',
    loadError: 'تعذر تحميل بيانات الأفحل.',
    saveError: 'تعذر حفظ الفحل.',
    chooseSource: 'اختر طريقة إضافة الفحل',
    inHouseHint: 'اختيار فحل مسجل من قائمة الخيل وسيتم تعبئة بياناته تلقائياً.',
    externalHint: 'استخدمه عند وجود سائل منوي أو فحل خارجي غير مسجل ضمن خيل المربط.',
    eligibleCandidates: 'أفحل متاحة للإضافة',
    selectedHorse: 'الفحل المختار',
  },
  en: {
    title: 'Stallion Management',
    subtitle: 'Add, edit, and deactivate stallions shown in the reproduction insemination list.',
    add: 'Add stallion',
    edit: 'Edit stallion',
    refresh: 'Refresh',
    activeList: 'Insemination list',
    candidates: 'Registered stallion candidates',
    candidatesHint: 'Choose a stallion from registered horses or add an external stallion manually.',
    registered: 'Registered stallions',
    registeredHint: 'This is the list shown when choosing a stallion inside an insemination procedure.',
    total: 'Total stallions',
    inHouse: 'In-house',
    external: 'External',
    used: 'Inseminations',
    noData: 'No data.',
    type: 'Stallion type',
    selectHorse: 'Select registered stallion',
    nameEn: 'English name',
    nameAr: 'Arabic name',
    country: 'Origin country',
    owner: 'Owner',
    notes: 'Notes',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    save: 'Save',
    cancel: 'Cancel',
    deactivate: 'Deactivate',
    deactivateTitle: 'Deactivate stallion',
    deactivateConfirm: 'The stallion will be hidden from the insemination list while keeping previous records.',
    saved: 'Stallion saved.',
    deactivated: 'Stallion deactivated.',
    duplicate: 'This stallion is already registered.',
    required: 'English name, Arabic name, and origin country are required.',
    loadError: 'Failed to load stallions.',
    saveError: 'Failed to save stallion.',
    chooseSource: 'Choose how to add the stallion',
    inHouseHint: 'Select a registered horse and its details will be filled automatically.',
    externalHint: 'Use this when semen or a stallion source is external and not registered in the stable horses.',
    eligibleCandidates: 'Eligible candidates',
    selectedHorse: 'Selected stallion',
  },
};

function emptyForm() {
  return {
    stallion_id: null,
    horse_id: null,
    name: '',
    name_ar: '',
    origin_country: '',
    owner_id: '',
    notes: '',
    is_inhouse: 1,
    is_active: 1,
  };
}

function langKey(language) {
  return String(language || '').startsWith('ar') ? 'ar' : 'en';
}

function displayName(row) {
  return [row?.name_ar, row?.name].filter(Boolean).join(' | ') || '-';
}

export default function StallionForm() {
  const { t, i18n } = useTranslation();
  const lang = langKey(i18n.language);
  const text = { ...COPY[lang], ...(t('stallionManagement', { returnObjects: true, defaultValue: {} }) || {}) };
  const [horseCandidates, setHorseCandidates] = useState([]);
  const [registered, setRegistered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const activeRegistered = useMemo(
    () => registered.filter((row) => Number(row.is_active ?? 1) === 1),
    [registered],
  );

  const registeredHorseIds = useMemo(
    () => new Set(activeRegistered.map((row) => Number(row.horse_id)).filter(Boolean)),
    [activeRegistered],
  );

  const eligibleCandidates = useMemo(
    () => horseCandidates.filter((horse) => !registeredHorseIds.has(Number(horse.horse_id)) || Number(horse.horse_id) === Number(form.horse_id)),
    [form.horse_id, horseCandidates, registeredHorseIds],
  );

  const stats = useMemo(() => ({
    total: activeRegistered.length,
    inHouse: activeRegistered.filter((row) => Number(row.is_inhouse) === 1).length,
    external: activeRegistered.filter((row) => Number(row.is_inhouse) !== 1).length,
    used: activeRegistered.reduce((sum, row) => sum + Number(row.insemination_count || 0), 0),
  }), [activeRegistered]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [horsesRes, registeredRes] = await Promise.all([
        api.get('/stallions'),
        api.get('/stallionslist/manage'),
      ]);
      setHorseCandidates(Array.isArray(horsesRes.data) ? horsesRes.data : []);
      setRegistered(Array.isArray(registeredRes.data) ? registeredRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || text.loadError);
    } finally {
      setLoading(false);
    }
  }, [text.loadError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setForm(emptyForm());
    setDialogOpen(true);
    setError('');
  };

  const openEdit = (row) => {
    setForm({
      stallion_id: row.stallion_id,
      horse_id: row.horse_id || null,
      name: row.name || '',
      name_ar: row.name_ar || '',
      origin_country: row.origin_country || '',
      owner_id: row.owner_id || '',
      notes: row.notes || '',
      is_inhouse: Number(row.is_inhouse) === 1 ? 1 : 0,
      is_active: Number(row.is_active ?? 1) === 1 ? 1 : 0,
    });
    setDialogOpen(true);
  };

  const selectCandidate = (_event, option) => {
    if (!option) return;
    setForm((current) => ({
      ...current,
      horse_id: option.horse_id || null,
      name: option.name || '',
      name_ar: option.name_ar || '',
      origin_country: option.origin_country || '',
      owner_id: option.owner_id || '',
      is_inhouse: 1,
    }));
  };

  const saveStallion = async () => {
    if (!form.name || !form.name_ar || !form.origin_country) {
      setError(text.required);
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    const payload = {
      name: form.name,
      name_ar: form.name_ar,
      origin_country: form.origin_country,
      is_inhouse: Number(form.is_inhouse) === 1,
      notes: form.notes,
      horse_id: Number(form.is_inhouse) === 1 ? form.horse_id : null,
      owner_id: Number(form.is_inhouse) === 1 ? Number(form.owner_id || 0) || null : null,
      is_active: Number(form.is_active) === 1 ? 1 : 0,
    };
    try {
      if (form.stallion_id) {
        await api.put(`/stallionslist/${form.stallion_id}`, payload);
      } else {
        await api.post('/stallions', payload);
      }
      setDialogOpen(false);
      setForm(emptyForm());
      setMessage(text.saved);
      await loadData();
    } catch (err) {
      setError(err?.response?.status === 409 ? text.duplicate : (err?.response?.data?.message || err?.response?.data?.error || text.saveError));
    } finally {
      setSaving(false);
    }
  };

  const deactivateStallion = async () => {
    if (!deleteTarget?.stallion_id) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.delete(`/stallionslist/${deleteTarget.stallion_id}`);
      setDeleteTarget(null);
      setMessage(text.deactivated);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || text.saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="smart-page-container" dir={lang === 'ar' ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={text.title}
        subtitle={text.subtitle}
        actions={(
          <Stack direction="row" spacing={1}>
            <Button onClick={loadData} startIcon={<RefreshIcon />} sx={{ color: COLORS.gold, fontWeight: 900 }}>
              {text.refresh}
            </Button>
            <Button onClick={openCreate} variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>
              {text.add}
            </Button>
          </Stack>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Grid container spacing={2.2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={3}><Metric icon={<MaleIcon />} label={text.total} value={stats.total} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<MaleIcon />} label={text.inHouse} value={stats.inHouse} color={COLORS.blue} soft={COLORS.blueSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<MaleIcon />} label={text.external} value={stats.external} color={COLORS.green} soft={COLORS.greenSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<MaleIcon />} label={text.used} value={stats.used} color={COLORS.rose} soft={COLORS.roseSoft} /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={1.5} sx={{ p: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.registered}</Typography>
            <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.registeredHint}</Typography>
          </Box>
          <Chip label={`${activeRegistered.length} ${text.activeList}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }} />
        </Stack>
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
            <Box component="thead" sx={{ bgcolor: COLORS.tableHead }}>
              <Box component="tr">
                {[text.nameAr, text.nameEn, text.type, text.country, text.owner, text.used, text.status, ''].map((head) => (
                  <Box component="th" key={head || 'actions'} sx={{ p: 1.5, color: '#fff', fontWeight: 900, textAlign: 'start', borderBottom: `1px solid ${COLORS.goldHover}` }}>{head}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {activeRegistered.length ? activeRegistered.map((row) => (
                <Box component="tr" key={row.stallion_id} sx={{ borderBottom: `1px solid ${COLORS.line}` }}>
                  <Cell strong>{row.name_ar || '-'}</Cell>
                  <Cell>{row.name || '-'}</Cell>
                  <Cell><Chip size="small" label={Number(row.is_inhouse) === 1 ? text.inHouse : text.external} sx={{ bgcolor: Number(row.is_inhouse) === 1 ? COLORS.blueSoft : COLORS.greenSoft, fontWeight: 800 }} /></Cell>
                  <Cell>{row.origin_country || '-'}</Cell>
                  <Cell>{row.owner_name?.trim() || row.owner_id || '-'}</Cell>
                  <Cell>{row.insemination_count || 0}</Cell>
                  <Cell><Chip size="small" label={text.active} sx={{ bgcolor: COLORS.greenSoft, color: COLORS.green, fontWeight: 800 }} /></Cell>
                  <Cell align="end">
                    <IconButton onClick={() => openEdit(row)} sx={{ color: COLORS.blue }}><EditIcon /></IconButton>
                    <IconButton onClick={() => setDeleteTarget(row)} sx={{ color: COLORS.rose }}><DeleteOutlineIcon /></IconButton>
                  </Cell>
                </Box>
              )) : (
                <Box component="tr"><Cell colSpan={8}>{text.noData}</Cell></Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mt: 2 }}>
        <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.candidates}</Typography>
        <Typography variant="body2" sx={{ color: COLORS.muted, mb: 1.5 }}>{text.candidatesHint}</Typography>
        <Grid container spacing={1.2}>
          {eligibleCandidates.slice(0, 8).map((horse) => (
            <Grid item xs={12} md={3} key={horse.horse_id}>
              <Paper elevation={0} sx={{ p: 1.2, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft }}>
                <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{displayName(horse)}</Typography>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{horse.rfid_chip_number || '-'}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{form.stallion_id ? text.edit : text.add}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: COLORS.text, fontWeight: 900 }}>{text.chooseSource}</Typography>
              <Grid container spacing={1.5}>
                {[
                  { value: 1, label: text.inHouse, hint: text.inHouseHint, color: COLORS.blue, soft: COLORS.blueSoft },
                  { value: 0, label: text.external, hint: text.externalHint, color: COLORS.green, soft: COLORS.greenSoft },
                ].map((option) => {
                  const selected = Number(form.is_inhouse) === option.value;
                  return (
                    <Grid item xs={12} sm={6} key={option.value}>
                      <Paper
                        elevation={0}
                        role="button"
                        tabIndex={0}
                        onClick={() => setForm((value) => ({
                          ...value,
                          is_inhouse: option.value,
                          horse_id: null,
                          owner_id: '',
                          name: '',
                          name_ar: '',
                          origin_country: '',
                        }))}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setForm((value) => ({
                              ...value,
                              is_inhouse: option.value,
                              horse_id: null,
                              owner_id: '',
                              name: '',
                              name_ar: '',
                              origin_country: '',
                            }));
                          }
                        }}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderRadius: 2,
                          border: `1px solid ${selected ? option.color : COLORS.line}`,
                          bgcolor: selected ? option.soft : COLORS.card,
                          minHeight: 112,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                          <MaleIcon sx={{ color: option.color }} />
                          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{option.label}</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: COLORS.muted }}>{option.hint}</Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            {Number(form.is_inhouse) === 1 && (
              <Stack spacing={1}>
                <Autocomplete
                  options={eligibleCandidates}
                  getOptionLabel={(option) => displayName(option)}
                  value={horseCandidates.find((horse) => Number(horse.horse_id) === Number(form.horse_id)) || null}
                  onChange={selectCandidate}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.horse_id}>
                      <Box>
                        <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{displayName(option)}</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.muted }}>
                          {option.rfid_chip_number || '-'} {option.origin_country ? ` · ${option.origin_country}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={text.selectHorse}
                      helperText={`${eligibleCandidates.length} ${text.eligibleCandidates}`}
                      fullWidth
                    />
                  )}
                />
                {form.horse_id && (
                  <Paper elevation={0} sx={{ p: 1.2, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft }}>
                    <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 900 }}>{text.selectedHorse}</Typography>
                    <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{[form.name_ar, form.name].filter(Boolean).join(' | ')}</Typography>
                    <Typography variant="body2" sx={{ color: COLORS.muted }}>{form.origin_country || '-'}</Typography>
                  </Paper>
                )}
              </Stack>
            )}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField label={text.nameAr} value={form.name_ar} onChange={(event) => setForm((value) => ({ ...value, name_ar: event.target.value }))} fullWidth required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label={text.nameEn} value={form.name} onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))} fullWidth required />
              </Grid>
            </Grid>
            <TextField label={text.country} value={form.origin_country} onChange={(event) => setForm((value) => ({ ...value, origin_country: event.target.value }))} fullWidth required />
            {Number(form.is_inhouse) === 1 && (
              <TextField label={text.owner} value={form.owner_id || ''} onChange={(event) => setForm((value) => ({ ...value, owner_id: event.target.value }))} fullWidth />
            )}
            <TextField multiline rows={3} label={text.notes} value={form.notes || ''} onChange={(event) => setForm((value) => ({ ...value, notes: event.target.value }))} fullWidth />
            <FormControlLabel
              control={<Switch checked={Number(form.is_active) === 1} onChange={(event) => setForm((value) => ({ ...value, is_active: event.target.checked ? 1 : 0 }))} />}
              label={Number(form.is_active) === 1 ? text.active : text.inactive}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>{text.cancel}</Button>
          <Button disabled={saving} onClick={saveStallion} variant="contained" startIcon={<SaveIcon />} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.deactivateTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="warning">{text.deactivateConfirm}</Alert>
            <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{displayName(deleteTarget)}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>{text.cancel}</Button>
          <Button disabled={saving} onClick={deactivateStallion} variant="contained" startIcon={<DeleteOutlineIcon />} sx={{ bgcolor: COLORS.rose, fontWeight: 900, '&:hover': { bgcolor: COLORS.rose } }}>{text.deactivate}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Metric({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, display: 'flex', gap: 1.5, alignItems: 'center', minHeight: 104 }}>
      <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: soft, color, display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
        <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
      </Box>
    </Paper>
  );
}

function Cell({ children, align = 'start', strong = false, colSpan }) {
  return (
    <Box component="td" colSpan={colSpan} sx={{ p: 1.4, color: COLORS.text, fontWeight: strong ? 900 : 500, textAlign: align, verticalAlign: 'middle' }}>
      {children}
    </Box>
  );
}
